import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clapperboard, Save, RotateCcw, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import {
  useVideoProcessingSettings,
  useUpdateVideoProcessingSettings,
  useUploadVideoProcessingAsset,
  type VideoProcessingUploadSlot,
} from '../hooks/useVideoProcessingSettings';

function isAppAdminRole(role: string | undefined): boolean {
  const r = role?.toLowerCase().trim() ?? '';
  return r === 'admin' || r === 'administrator' || r === 'video_admin';
}

function AssetUploadControl(props: {
  slot: VideoProcessingUploadSlot;
  accept: string;
  hint: string;
  busy: boolean;
  disabled: boolean;
  onPick: (slot: VideoProcessingUploadSlot, files: FileList | null) => void;
}) {
  const { slot, accept, hint, busy, disabled, onPick } = props;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
        <Upload className="h-4 w-4" />
        {busy ? 'Uploading…' : 'Upload file'}
        <input
          type="file"
          accept={accept}
          className="hidden"
          disabled={disabled || busy}
          onChange={(e) => {
            onPick(slot, e.target.files);
            e.target.value = '';
          }}
        />
      </label>
      <span className="text-xs text-gray-500">{hint}</span>
    </div>
  );
}

/** Build PATCH payload: only include keys user changed from loaded snapshot; empty string → null (clear override). */
function buildPayload(
  snap: Record<string, string | undefined>,
  cur: Record<string, string | undefined>
): Record<string, unknown> | null {
  const keys = [
    'width',
    'height',
    'frameRate',
    'introVideoPath',
    'outroVideoPath',
    'masterAudioPath',
    'overlayLogoPath',
    'separatorImagePath',
    'separatorVideoPath',
    'separatorMaxVideoSeconds',
  ] as const;
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const before = snap[k];
    const after = cur[k];
    if (before === after) continue;
    if (k === 'width' || k === 'height' || k === 'frameRate') {
      const t = (after ?? '').trim();
      if (t === '') out[k] = null;
      else {
        const n = parseInt(t, 10);
        if (Number.isNaN(n)) continue;
        out[k] = n;
      }
    } else if (k === 'separatorMaxVideoSeconds') {
      const t = (after ?? '').trim();
      if (t === '') out[k] = null;
      else {
        const n = parseFloat(t);
        if (Number.isNaN(n)) continue;
        out[k] = n;
      }
    } else {
      const t = (after ?? '').trim();
      out[k] = t === '' ? null : t;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

const VideoProcessingSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const canAdmin = isAppAdminRole(user?.role);
  const { data, isLoading, error } = useVideoProcessingSettings(canAdmin);
  const updateMutation = useUpdateVideoProcessingSettings();
  const uploadMutation = useUploadVideoProcessingAsset();
  const [uploadingSlot, setUploadingSlot] = useState<VideoProcessingUploadSlot | null>(null);

  const [snap, setSnap] = useState<Record<string, string | undefined>>({});
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [frameRate, setFrameRate] = useState('');
  const [introVideoPath, setIntroVideoPath] = useState('');
  const [outroVideoPath, setOutroVideoPath] = useState('');
  const [masterAudioPath, setMasterAudioPath] = useState('');
  const [overlayLogoPath, setOverlayLogoPath] = useState('');
  const [separatorImagePath, setSeparatorImagePath] = useState('');
  const [separatorVideoPath, setSeparatorVideoPath] = useState('');
  const [separatorMaxVideoSeconds, setSeparatorMaxVideoSeconds] = useState('');

  // Sync form from server only when *stored* row fields change — not on every react-query `data`
  // refetch (new object reference), so background refetches do not wipe unsaved edits.
  useEffect(() => {
    if (!data?.stored || data.effective == null) return;
    const s = data.stored;
    const e = data.effective;
    const next = {
      width: s.width != null ? String(s.width) : String(e.width),
      height: s.height != null ? String(s.height) : String(e.height),
      frameRate: s.frameRate != null ? String(s.frameRate) : String(e.frameRate),
      introVideoPath: s.introVideoPath ?? e.introVideoPath ?? '',
      outroVideoPath: s.outroVideoPath ?? e.outroVideoPath ?? '',
      masterAudioPath: s.masterAudioPath ?? e.masterAudioPath ?? '',
      overlayLogoPath: s.overlayLogoPath ?? e.overlayLogoPath ?? '',
      separatorImagePath: s.separatorImagePath ?? e.separatorImagePath ?? '',
      separatorVideoPath: s.separatorVideoPath ?? e.separatorVideoPath ?? '',
      separatorMaxVideoSeconds:
        s.separatorMaxVideoSeconds != null
          ? String(s.separatorMaxVideoSeconds)
          : String(e.separatorMaxVideoSeconds),
    };
    setSnap(next);
    setWidth(next.width);
    setHeight(next.height);
    setFrameRate(next.frameRate);
    setIntroVideoPath(next.introVideoPath);
    setOutroVideoPath(next.outroVideoPath);
    setMasterAudioPath(next.masterAudioPath);
    setOverlayLogoPath(next.overlayLogoPath);
    setSeparatorImagePath(next.separatorImagePath);
    setSeparatorVideoPath(next.separatorVideoPath);
    setSeparatorMaxVideoSeconds(next.separatorMaxVideoSeconds);
  }, [
    data?.stored?.updatedAt,
    data?.stored?.width,
    data?.stored?.height,
    data?.stored?.frameRate,
    data?.stored?.introVideoPath,
    data?.stored?.outroVideoPath,
    data?.stored?.masterAudioPath,
    data?.stored?.overlayLogoPath,
    data?.stored?.separatorImagePath,
    data?.stored?.separatorVideoPath,
    data?.stored?.separatorMaxVideoSeconds,
  ]);

  const cur = useMemo(
    () => ({
      width,
      height,
      frameRate,
      introVideoPath,
      outroVideoPath,
      masterAudioPath,
      overlayLogoPath,
      separatorImagePath,
      separatorVideoPath,
      separatorMaxVideoSeconds,
    }),
    [
      width,
      height,
      frameRate,
      introVideoPath,
      outroVideoPath,
      masterAudioPath,
      overlayLogoPath,
      separatorImagePath,
      separatorVideoPath,
      separatorMaxVideoSeconds,
    ]
  );

  if (!canAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleSave = async () => {
    const payload = buildPayload(snap, cur);
    if (!payload) {
      toast('Nothing changed compared to when this form was loaded.');
      return;
    }
    try {
      await updateMutation.mutateAsync(payload);
      toast.success('Video processing settings saved.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message ?? 'Failed to save settings');
    }
  };

  const handleResetDbOverrides = async () => {
    try {
      await updateMutation.mutateAsync({ resetAllToConfigDefaults: true });
      toast.success('Database overrides cleared; appsettings apply.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message ?? 'Failed to reset');
    }
  };

  const handleAssetUpload = async (slot: VideoProcessingUploadSlot, files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setUploadingSlot(slot);
    try {
      const res = await uploadMutation.mutateAsync({ slot, file });
      toast.success(res.message);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message ?? 'Upload failed');
    } finally {
      setUploadingSlot(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-red-600">
        Could not load video processing settings. Ensure you are signed in as an admin.
      </div>
    );
  }

  const eff = data.effective;
  const fb = data.configFallback;
  const rf = data.resolvedFiles;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center space-x-4 mb-6">
        <Link to="/">
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back
          </Button>
        </Link>
      </div>

      <div className="flex items-start gap-3 mb-2">
        <Clapperboard className="h-9 w-9 text-primary-600 shrink-0 mt-1" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Video processing</h1>
          <p className="mt-1 text-gray-600">
            Override combine pipeline settings stored in the database. Leave a field empty and save to use{' '}
            <span className="font-medium">appsettings.json</span> for that value. Upload media below to store files on
            the server and set paths automatically, or paste paths manually (relative to the API content root or
            absolute).
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="space-y-4 md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-800">Output & intro</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Width override</span>
              <input
                type="text"
                inputMode="numeric"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder={`effective: ${eff.width}`}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Height override</span>
              <input
                type="text"
                inputMode="numeric"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder={`effective: ${eff.height}`}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Frame rate override</span>
              <input
                type="text"
                inputMode="numeric"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={frameRate}
                onChange={(e) => setFrameRate(e.target.value)}
                placeholder={`effective: ${eff.frameRate}`}
              />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Intro video path</span>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={introVideoPath}
              onChange={(e) => setIntroVideoPath(e.target.value)}
              placeholder={eff.introVideoPath ?? fb.videoOutput.introVideoPath ?? '(none)'}
            />
            <AssetUploadControl
              slot="intro-video"
              accept="video/mp4,video/quicktime,video/webm,.mkv,.mp4,.mov,.webm"
              hint="MP4, MOV, WebM, MKV — max ~256 MB"
              busy={uploadingSlot === 'intro-video'}
              disabled={updateMutation.isLoading}
              onPick={handleAssetUpload}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Outro video path</span>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={outroVideoPath}
              onChange={(e) => setOutroVideoPath(e.target.value)}
              placeholder={eff.outroVideoPath ?? fb.videoOutput.outroVideoPath ?? '(optional)'}
            />
            <AssetUploadControl
              slot="outro-video"
              accept="video/mp4,video/quicktime,video/webm,.mkv,.mp4,.mov,.webm"
              hint="MP4, MOV, WebM, MKV — max ~256 MB"
              busy={uploadingSlot === 'outro-video'}
              disabled={updateMutation.isLoading}
              onPick={handleAssetUpload}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Master audio path</span>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={masterAudioPath}
              onChange={(e) => setMasterAudioPath(e.target.value)}
              placeholder={eff.masterAudioPath ?? fb.videoOutput.masterAudioPath ?? '(optional)'}
            />
            <AssetUploadControl
              slot="master-audio"
              accept="audio/*,.m4a,.aac,.flac"
              hint="Loops if shorter than video; stops when video ends. MP3, WAV, AAC, M4A, OGG, FLAC"
              busy={uploadingSlot === 'master-audio'}
              disabled={updateMutation.isLoading}
              onPick={handleAssetUpload}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Watermark logo (top-left on final video)</span>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={overlayLogoPath}
              onChange={(e) => setOverlayLogoPath(e.target.value)}
              placeholder={eff.overlayLogoPath ?? fb.videoOutput.overlayLogoPath ?? 'assets/overlay-logo.png'}
            />
            <AssetUploadControl
              slot="overlay-logo"
              accept="image/png,image/webp,.png"
              hint="PNG with transparency recommended — default ETR logo"
              busy={uploadingSlot === 'overlay-logo'}
              disabled={updateMutation.isLoading}
              onPick={handleAssetUpload}
            />
            {rf.overlayLogoExists && (
              <p className="mt-1 text-xs text-green-700">Resolved: {rf.overlayLogoAbsolute}</p>
            )}
          </label>
        </div>

        <div className="space-y-4 md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-800">Separator</h2>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Separator image path</span>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={separatorImagePath}
              onChange={(e) => setSeparatorImagePath(e.target.value)}
              placeholder={eff.separatorImagePath ?? fb.separator.imagePath ?? '(optional)'}
            />
            <AssetUploadControl
              slot="separator-image"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              hint="PNG, JPEG, WebP — max ~25 MB"
              busy={uploadingSlot === 'separator-image'}
              disabled={updateMutation.isLoading}
              onPick={handleAssetUpload}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Separator video path</span>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={separatorVideoPath}
              onChange={(e) => setSeparatorVideoPath(e.target.value)}
              placeholder={eff.separatorVideoPath ?? fb.separator.videoPath ?? '(optional)'}
            />
            <AssetUploadControl
              slot="separator-video"
              accept="video/mp4,video/quicktime,video/webm,.mkv,.mp4,.mov,.webm"
              hint="MP4, MOV, WebM, MKV — max ~256 MB"
              busy={uploadingSlot === 'separator-video'}
              disabled={updateMutation.isLoading}
              onPick={handleAssetUpload}
            />
          </label>
          <label className="block max-w-xs">
            <span className="text-sm font-medium text-gray-700">Max separator video (seconds)</span>
            <input
              type="text"
              inputMode="decimal"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={separatorMaxVideoSeconds}
              onChange={(e) => setSeparatorMaxVideoSeconds(e.target.value)}
              placeholder={`effective: ${eff.separatorMaxVideoSeconds}`}
            />
          </label>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button
          variant="primary"
          leftIcon={<Save className="h-4 w-4" />}
          onClick={handleSave}
          disabled={updateMutation.isLoading || !!uploadingSlot}
        >
          Save changes
        </Button>
        <Button
          variant="outline"
          leftIcon={<RotateCcw className="h-4 w-4" />}
          onClick={handleResetDbOverrides}
          disabled={updateMutation.isLoading || !!uploadingSlot}
        >
          Clear all DB overrides
        </Button>
      </div>

      <div className="mt-10 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
        {data.uploadDirectoryHint ? (
          <p className="mb-3 text-xs text-gray-600">
            <span className="font-semibold text-gray-800">Upload folder (server): </span>
            <code className="break-all">{data.uploadDirectoryHint}</code>
          </p>
        ) : null}
        <p className="font-semibold text-gray-900">Resolved files</p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>
            Intro: {rf.introExists ? 'found' : 'missing'}{' '}
            {rf.introAbsolute ? <code className="text-xs break-all">{rf.introAbsolute}</code> : null}
          </li>
          <li>
            Outro: {rf.outroExists ? 'found' : 'not set / missing'}{' '}
            {rf.outroAbsolute ? <code className="text-xs break-all">{rf.outroAbsolute}</code> : null}
          </li>
          <li>
            Master audio: {rf.masterAudioExists ? 'found' : 'not set / missing'}{' '}
            {rf.masterAudioAbsolute ? (
              <code className="text-xs break-all">{rf.masterAudioAbsolute}</code>
            ) : null}
          </li>
          <li>Separator image: {rf.separatorImageExists ? 'found' : 'missing (fallback rules may apply)'}</li>
          <li>Separator video: {rf.separatorVideoExists ? 'found' : 'not set / missing'}</li>
        </ul>
        <p className="mt-4 font-semibold text-gray-900">Appsettings fallback (read-only)</p>
        <pre className="mt-2 overflow-x-auto rounded bg-white p-3 text-xs shadow-inner">
          {JSON.stringify({ videoOutput: fb.videoOutput, separator: fb.separator }, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default VideoProcessingSettingsPage;
